






























































































































































































import {
  SfButton,
  SfCheckbox,
  SfDivider,
  SfHeading,
  SfImage,
  SfLink,
  SfPrice,
  SfProperty,
  SfTable,
  SfNotification,
  SfLoader,
} from '~/components';

import {
  defineComponent,
  ref,
  computed,
  useRouter,
  useContext,
  onMounted,
} from '@nuxtjs/composition-api';

import cartGetters from '~/modules/checkout/getters/cartGetters';
import {
  useImage,
  useMakeOrder,
  useCart,
  usePaymentProvider,
  useApi,
  useNetwork
} from '~/composables';

import getShippingMethodPrice from '~/helpers/checkout/getShippingMethodPrice';
import { removeItem } from '~/helpers/asyncLocalStorage';

import type {
  BundleCartItem,
  ConfigurableCartItem
} from '~/modules/GraphQL/types';

import { sendEcommerce } from '~/utilities/helpers';
import { useGiftWrapStore } from '~/modules/checkout/stores/giftWrap';
import { PaymentTypeEnums } from '~/modules/checkout/stripe/types';

export default defineComponent({
  name: 'ReviewOrderAndPayment',
  components: {
    SfLoader,
    SfHeading,
    SfTable,
    SfCheckbox,
    SfButton,
    SfDivider,
    SfPrice,
    SfProperty,
    SfLink,
    SfImage,
    SfNotification,
    StripePaymentProvider: () => import('~/modules/checkout/components/StripePaymentProvider.vue'),
  },
  emits: ['make-order', 'process-order-status'],
  props: {
    isOrderProcessing: { type: Boolean, default: false },
  },
  setup(_, { emit }) {
    const giftWrapStore = useGiftWrapStore();
    const {
      getMagentoImage,
      imageSizes
    } = useImage();

    const { isOnline } = useNetwork();

    const {
      cart,
      load: loadCart,
      setCart
    } = useCart();

    const {
      setGiftWrapDetails,
      loading,
      make
    } = useMakeOrder();

    const { mutate } = useApi();

    const { save } = usePaymentProvider();

    const { app } = useContext();
    const router = useRouter();

    const StripePaymentProviderRef = ref(null);
    const isPaymentReady = ref(false);
    const terms = ref(false);
    const getAttributes = (product: ConfigurableCartItem) => product.configurable_options || [];
    const getBundles = (product: BundleCartItem) => product.bundle_options?.map((b) => b.values).flat() || [];
    const isStripeNotificationVisible = ref(false);
    const stripeNotificationErrorText = ref('');
    const paymentType = ref('');
    const isStripeReady = ref(false);
    const order = ref();

    const isOnlineNotificationVisible = ref(false);

    const klarnaAvailableCountries = ['AT', 'BE', 'DE', 'DK', 'ES', 'FI', 'GB', 'IE', 'IT', 'NL', 'NO', 'SE', 'US', 'FR', 'CZ', 'GR', 'AU', 'NZ', 'CA', 'PL', 'PT', 'CH'];

    const closeStripeNotification = () => {
      isStripeNotificationVisible.value = false;
    };

    const handleStripeStatus = (completeStatus) => {
      const { type } = completeStatus.value;
      const country = cart.value.billing_address.country.code;

      closeStripeNotification();
      paymentType.value = type;

      const setPaymentReady = (isReady) => {
        isStripeReady.value = isReady;
        isPaymentReady.value = isReady;
      };

      switch (type) {
        case 'card':
        case 'paypal':
        case 'google_pay':
        case 'apple_pay':
          setPaymentReady(completeStatus.complete);
          break;
        case 'klarna':
          if (klarnaAvailableCountries.includes(country)) {
            setPaymentReady(completeStatus.complete);
          } else {
            stripeNotificationErrorText.value = `Klarna is not available for customers in ${country}.`;
            isStripeNotificationVisible.value = true;
          }
          break;
        case 'external_tabby':
          setPaymentReady(completeStatus.complete);
          break;
        default:
          console.error(`Unsupported payment type: ${type}`);
      }
    };

    const getTabbyUrl = async (orderId: string) => {
      const query = `
        mutation {
          generateTabbyCheckoutUrl(input: { order_number: "${orderId}" }) {
            url
          }
        }`;

      return await mutate(query);
    };

    const processOrder = async () => {
      try {

        if (!isOnline.value) {
          isOnlineNotificationVisible.value = true;
          return;
        }

        emit('process-order-status', true);
        isStripeNotificationVisible.value = false;
        isOnlineNotificationVisible.value = false;

        await setGiftWrapDetails({
          cart_id: cart.value.id,
          personal_message: giftWrapStore.giftWrap.personalMessage,
          is_receipt_print: giftWrapStore.giftWrap.needEmailReceiptInsteadPrinting,
        });

        if (paymentType.value === PaymentTypeEnums.tabby) {
          await save({
            paymentMethod: {
              code: 'tabby_installments',
            }
          });
          order.value = await make();
          if (order.value?.order?.order_number) emit('make-order', order.value.order.order_number || '');
          const tabbyUrl = await getTabbyUrl(order.value.order.order_number);
          //@ts-ignore
          if (tabbyUrl.data.generateTabbyCheckoutUrl.url) {
            //@ts-ignore
            window.location.href = tabbyUrl.data.generateTabbyCheckoutUrl.url;
          }
          return;
        }

        const stripeStatus = await StripePaymentProviderRef.value.triggerStripe();

        if (!stripeStatus || stripeStatus.message) {
          stripeNotificationErrorText.value = stripeStatus?.message || 'Something goes wrong';
          isStripeNotificationVisible.value = true;
          emit('process-order-status', false);
          return;
        }

        setCart(null);

        const thankYouRoute = app.localeRoute({
          name: 'thank-you',
          query: {
            order: stripeStatus?.order_number,
          },
        });

        app.$vsf.$magento.config.state.removeCartId();
        await loadCart();
        await removeItem('checkout');
        await router.push(thankYouRoute);
        emit('process-order-status', false);

      } catch (error) {
        console.warn('Error: ', error);
      }
    };

    const discounts = computed(() => cartGetters.getDiscounts(cart.value));
    const hasDiscounts = computed(() => discounts.value.length > 0);
    const discountsAmount = computed(() => -1 * discounts.value.reduce((a, el) => el.value + a, 0),);

    // Ecommerce and Pixels
    const productsData = cart.value?.items?.map(
      item => Object.assign(
        item.product,
        { quantity: item.quantity },
        { configurable_product_options_selection: { variant: { uid: item.uid } } }
      )
    ) || [];

    const ecommerceAdditional = {
      'value': cart.value?.prices.grand_total.value,
      'currency': app.$vsf.$magento.config.state.getCurrency().toUpperCase(),
    };

    const sendEcommerceAddPaymentInfo = () => {
      const additional = {
        'goods_id': cart.value?.items.map(item => item.product.sku),
        'goods_price': cart.value?.prices.grand_total.value,
      };

      sendEcommerce('add_payment_info', 'cart', productsData, additional, {
        ...ecommerceAdditional,
        payment_type: paymentType.value
      });
    };

    onMounted(async () => {
      await loadCart();
    });

    return {
      cart,
      cartGetters,
      discounts,
      hasDiscounts,
      discountsAmount,
      getShippingMethodPrice,
      isPaymentReady,
      loading,
      processOrder,
      products: computed(() => cartGetters.getItems(cart.value)),
      selectedShippingMethod: computed(() => cartGetters.getSelectedShippingMethod(cart.value)),
      tableHeaders: ['Description', 'Quantity', 'Amount'],
      terms,
      totals: computed(() => cartGetters.getTotals(cart.value)),
      getAttributes,
      getBundles,
      getMagentoImage,
      imageSizes,
      StripePaymentProviderRef,
      handleStripeStatus,
      isStripeNotificationVisible,
      closeStripeNotification,
      stripeNotificationErrorText,
      sendEcommerceAddPaymentInfo,
      paymentType,
      isStripeReady,
      isOnlineNotificationVisible
    };
  },
});
